/**
 * arbi usage today — show today's per-user usage against the daily cap
 *
 * Pre-flight cost check before launching long batch jobs. The endpoint is at
 * /v1/user/me/usage/today but no SDK helper wraps it yet, so we call it via
 * the typed openapi-fetch client directly.
 */

import { Command } from 'commander'
import { runAction, resolveAuth, printJson, suggestSubcommandAndExit } from '../helpers.js'
import { label, dim } from '../output.js'

export function registerUsageCommand(program: Command): void {
  const usage = program.command('usage').description("Show today's usage against the daily cap")

  usage
    .command('today')
    .description("Today's tokens used / remaining (for the current user)")
    .option('--json', 'Output as JSON')
    .action((opts: { json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        // Cast through `unknown` to recover the openapi-fetch result shape —
        // strict overload resolution drops it for some path/op combos.
        const res = (await arbi.fetch.GET('/v1/user/me/usage/today')) as {
          data?: Record<string, unknown>
          error?: unknown
        }
        if (res.error) {
          throw new Error(`Failed to fetch usage: ${JSON.stringify(res.error)}`)
        }
        const data = res.data as Record<string, unknown>
        if (opts.json) {
          printJson(data)
          return
        }
        // Best-effort human render. Fields are forward-compatible: we display
        // whatever the server actually returns, so adding new caps server-side
        // doesn't require a CLI release.
        for (const [k, v] of Object.entries(data)) {
          label(`${k}:`, typeof v === 'object' ? JSON.stringify(v) : String(v))
        }
        dim('\nPass --json for machine-readable output.')
      })()
    )

  // Default action: today, with typo suggestion for unknown subcommands.
  usage
    .allowUnknownOption(true)
    .allowExcessArguments(true)
    .action(async () => {
      const tail = usage.args ?? []
      const subcommands = usage.commands.map((c) => c.name())
      const firstNonFlag = tail.find((a) => !a.startsWith('-'))
      if (firstNonFlag && !subcommands.includes(firstNonFlag)) {
        suggestSubcommandAndExit('usage', firstNonFlag, subcommands)
      }
      await usage.commands.find((c) => c.name() === 'today')!.parseAsync(tail, { from: 'user' })
    })
}
